// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AttributesSpecificationsSection from "@pace/App/Components/ProductDetails/AttributesSpecificationsSection";
import parse from "html-react-parser";
import get from "lodash/get";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    // showDocuments = "showDocuments",
    // showAttributes = "showAttributes",
    // attributesPosition = "attributesPosition",
    // documentsPosition = "documentsPosition",
    // XNG-Change
    splitTable = "splitTable",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // Xng-Changes
    accordionWrapper?: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
};

const styles = specificationsStyles;

// XNG-Change: reordering the specifications
const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, properties },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition, splitTable },
}) => {
    // XNG-Change: Get ordered mapped accordions
    // TODO: BE will pullout Tools make sure this works
    
    /**
     * Xng-Change PAC-1234
     * Adjust product data features split by "|" adding "." for each line
     * @param features 
     * @returns 
     */
    const transformProductFeatures = (features: string) => {
        if(!features) {
            return "";
        }

        // Split the features by "|"
        const featureList = features.split("|");

        // Join the features with a <li> tag
        const transformedFeatures = featureList.map((feature, index) => {
            return `<li>${feature}</li>`;
        }).join('');

        return `<ul>${transformedFeatures}</ul>`;
    }

    const mappedAccordions = [
        {
            title: translate("Overview"),
            value: properties.erpDescription,
        },
        {
            title: translate("Product Features"),
            value: transformProductFeatures(properties.features),
        },
        {
            title: translate("Specifications"),
            props: {
                specifications,
                attributeTypes,
            },
            isShown: !!specifications?.length || !!attributeTypes?.length,
        },
        {
            title: translate("Tools"),
            value: properties.tools,
        },
        {
            title: translate("Includes"),
            value: properties.includes,
        },
        {
            title: translate("Warranty"),
            value: properties.warranty,
        },
        {
            title: translate("Standards / Approvals"),
            value: properties.standards,
        },
        // XNG-Changes: PAC-1372
        {
            title: translate("Application"),
            value: properties.application,
        },
    ];
    // const renderingDocuments = showDocuments && documents && documents.length > 0;
    // const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    // const renderingSpecifications = specifications && specifications.length > 0;
    // const documentsFirst = documentsPosition === "first";
    // const attributesFirst = attributesPosition === "first";

    // if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
    //     return null;
    // }

    // const documentsAccordion = renderingDocuments && (
    //     <ManagedAccordionSection
    //         title={translate("Documents")}
    //         data-test-selector="productDetails_specifications_documents"
    //         initialExpanded={
    //             ((!attributesFirst || !renderingAttributes) && documentsFirst) ||
    //             (!renderingSpecifications && !renderingAttributes)
    //         }
    //         {...styles.specificationsAccordionSection}
    //     >
    //         <GridContainer {...styles.documentsContainer}>
    //             {documents!.map(document => (
    //                 <GridItem key={document.id.toString()} {...styles.documentGridItem}>
    //                     <Link
    //                         href={document.filePath}
    //                         target="_new"
    //                         {...styles.documentLink}
    //                         data-test-selector={`productDetails_${document.id}`}
    //                     >
    //                         <IconMemo {...styles.documentIcon} />
    //                         <Typography {...styles.documentNameText}>{document.name}</Typography>
    //                     </Link>
    //                 </GridItem>
    //             ))}
    //         </GridContainer>
    //     </ManagedAccordionSection>
    // );
    // const attributesAccordion = renderingAttributes && (
    //     <ManagedAccordionSection
    //         title={translate("Attributes")}
    //         initialExpanded={attributesFirst || (!renderingSpecifications && !renderingDocuments)}
    //         data-test-selector="productDetails_specifications_attributes"
    //         {...styles.specificationsAccordionSection}
    //     >
    //         {brand && (
    //             <StyledWrapper {...styles.attributeTypeWrapper}>
    //                 <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}:&nbsp;</Typography>
    //                 <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
    //             </StyledWrapper>
    //         )}
    //         {attributeTypes &&
    //             attributeTypes.map(attributeType => (
    //                 <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
    //                     <Typography {...styles.attributeTypeLabelText}>
    //                         {attributeType.label || attributeType.name}:&nbsp;
    //                     </Typography>
    //                     <Typography {...styles.attributeTypeValuesText}>
    //                         {attributeType
    //                             .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
    //                             .join(", ")}
    //                     </Typography>
    //                 </StyledWrapper>
    //             ))}
    //     </ManagedAccordionSection>
    // );

    return (
        <Accordion
            headingLevel={2}
            data-test-selector="productDetails_specifications"
            {...styles.specificationsAccordion}
        >
            {/* {attributesFirst && attributesAccordion}
            {documentsFirst && documentsAccordion} */}
            {/* {renderingSpecifications &&
                specifications?.map((specification, index) => {
                    return (
                        <ManagedAccordionSection
                            // initialExpanded={
                            //     index === 0 &&
                            //     (!documentsFirst || !renderingDocuments) &&
                            //     (!attributesFirst || !renderingAttributes)
                            // }
                            key={specification.id.toString()}
                            title={specification.nameDisplay}
                            data-test-selector="productDetails_specifications_specification"
                            {...styles.specificationsAccordionSection}
                        >
                            {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                        </ManagedAccordionSection>
                    );
                })} */}
            {/* {!attributesFirst && attributesAccordion}
            {!documentsFirst && documentsAccordion} */}

            {/* XNG-change: show custom accordions */}

            {mappedAccordions.map(
                ({ title, value, props, isShown }) =>
                    ((props && isShown) || !!value) && (
                        // Xng-Changes: adding wrapper to fix print view issues
                        <StyledWrapper key={title} {...styles.accordionWrapper}>
                            <ManagedAccordionSection
                                title={title}
                                data-test-selector="productDetails_specifications_specification"
                                {...styles.specificationsAccordionSection}
                                initialExpanded={true}
                            >
                                {props ? (
                                    <AttributesSpecificationsSection
                                        {...props}
                                        brandName={brand?.name}
                                        splitTable={splitTable}
                                    />
                                ) : (
                                    parse(transformContentHtml(value), parserOptions)
                                )}
                            </ManagedAccordionSection>
                        </StyledWrapper>
                    ),
            )}
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            // {
            //     name: fields.showDocuments,
            //     displayName: "Show Documents",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 1,
            // },
            // {
            //     name: fields.documentsPosition,
            //     displayName: "Documents Position",
            //     editorTemplate: "RadioButtonsField",
            //     defaultValue: "first",
            //     horizontal: true,
            //     fieldType: "General",
            //     sortOrder: 2,
            //     options: [
            //         {
            //             displayName: "Display First",
            //             value: "first",
            //         },
            //         {
            //             displayName: "Display Last",
            //             value: "last",
            //         },
            //     ],
            //     isVisible: widget => widget.fields.showDocuments,
            // },
            // {
            //     name: fields.showAttributes,
            //     displayName: "Show Attributes",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 3,
            // },
            // {
            //     name: fields.attributesPosition,
            //     displayName: "Attributes Position",
            //     editorTemplate: "RadioButtonsField",
            //     defaultValue: "first",
            //     horizontal: true,
            //     fieldType: "General",
            //     sortOrder: 4,
            //     options: [
            //         {
            //             displayName: "Display First",
            //             value: "first",
            //         },
            //         {
            //             displayName: "Display Last",
            //             value: "last",
            //         },
            //     ],
            //     isVisible: widget => widget.fields.showAttributes,
            // },
            {
                name: fields.splitTable,
                displayName: "Split Table If Possible",
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
